import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'

// Import the generated route tree
import { AuthProvider, useAuth } from '@/contexts'
import { routeTree } from './routeTree.gen'
import type { i18n } from 'i18next'
import i18 from './i18n'

import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/utils/query-client.ts'

import './index.css'
import './fonts.css'
import { FeatureFlagProvider } from '@/contexts/FeatureFlagContext.tsx'
import qs from 'query-string'

// Create a new router instance
export const router = createRouter({
  routeTree,
  scrollToTopSelectors: ['main'],
  scrollRestoration: true,
  stringifySearch: search => {
    const queryString = qs.stringify(search, { arrayFormat: 'comma' })
    return queryString ? `?${queryString}` : ''
  },
  parseSearch: query => qs.parse(query, { arrayFormat: 'comma' }),
  context: {
    // auth will be passed down from App component
    auth: undefined!,
    // companies will be passed down from Companies route, if available
    company: undefined
  }
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function InnerApp() {
  const auth = useAuth()
  return <RouterProvider router={router} context={{ auth }} />
}

interface AppProps {
  i18n: i18n
}

// eslint-disable-next-line react-refresh/only-export-components
function App({ i18n }: AppProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <FeatureFlagProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <InnerApp />
          </AuthProvider>
        </QueryClientProvider>
      </FeatureFlagProvider>
    </I18nextProvider>
  )
}

// Render the app
const rootElement = document.getElementById('root')!

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <App i18n={i18} />
    </StrictMode>
  )
}
